/**
 * VÉRAULT Member Suite — CostStatusChip (Wave 26.25 · Phase 3c).
 * ──────────────────────────────────────────────────────────────
 * Calm, editorial chip for the 6 cost-status values. Lives on
 * ActivityCard (collapsed + expanded views). The visual language
 * stays in the warm-gold palette — no traffic-light reds/greens.
 *
 *   included      → quiet · neutral
 *   paid          → confirmed · sage-green
 *   deposit_due   → attention · gold
 *   optional      → discoverable · neutral italic
 *   pending       → in-flight · muted
 *   complimentary → gift · gold sparkle
 */
import React from "react";
import { Check, CircleDollarSign, Gift, Sparkles, Clock, ArrowDownToLine } from "lucide-react";

const STATUS_META = {
  included: {
    label: "Included",
    icon: Check,
    tone: "neutral",
    hint: "Baked into the trip price.",
  },
  paid: {
    label: "Paid",
    icon: Check,
    tone: "sage",
    hint: "Already settled.",
  },
  deposit_due: {
    label: "Deposit due",
    icon: ArrowDownToLine,
    tone: "gold",
    hint: "A partial payment is expected.",
  },
  optional: {
    label: "Optional",
    icon: Sparkles,
    tone: "muted-italic",
    hint: "An add-on you can choose.",
  },
  pending: {
    label: "Pending",
    icon: Clock,
    tone: "muted",
    hint: "Your advisor is preparing a quote.",
  },
  complimentary: {
    label: "Complimentary",
    icon: Gift,
    tone: "gold",
    hint: "On the house.",
  },
};

const TONE_CLASSES = {
  neutral: "border-[rgba(0,0,0,0.10)] bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-secondary))]",
  sage: "border-[rgba(99,120,99,0.25)] bg-[rgba(99,120,99,0.06)] text-[hsl(168,22%,28%)]",
  gold: "border-[hsl(var(--accent-gold))]/45 bg-[hsl(var(--accent-gold))]/8 text-[hsl(var(--foreground))]",
  "muted": "border-[rgba(0,0,0,0.06)] bg-[hsl(var(--background-paper))] text-[hsl(var(--foreground-muted))]",
  "muted-italic": "border-[rgba(0,0,0,0.06)] bg-transparent text-[hsl(var(--foreground-muted))] italic font-accent",
};

function formatAmount(amount, currency = "USD") {
  if (amount == null || amount === "") return null;
  const n = Number(amount);
  if (!isFinite(n)) return null;
  if (n === 0) return null; // included/complimentary are amount-less
  try {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency,
      maximumFractionDigits: n % 1 === 0 ? 0 : 2,
    }).format(n);
  } catch {
    return `${currency} ${n}`;
  }
}

/**
 * Compact chip — used inline beside the activity title.
 */
export function CostStatusChip({ status, amount, currency, showAmount = false, className = "" }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const Icon = meta.icon;
  const toneClass = TONE_CLASSES[meta.tone] || TONE_CLASSES.neutral;
  const amt = showAmount ? formatAmount(amount, currency) : null;
  return (
    <span
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full border font-mono text-[10px] uppercase tracking-[0.18em] ${toneClass} ${className}`}
      data-testid={`cost-chip-${status}`}
      data-cost-status={status}
      title={meta.hint}
    >
      <Icon className="size-3" strokeWidth={1.5} />
      <span>{meta.label}</span>
      {amt && (
        <span className="ml-0.5 not-italic font-semibold tracking-normal text-[11px] tabular-nums">
          {amt}
        </span>
      )}
    </span>
  );
}

/**
 * Expanded-view line — fuller detail for the open ActivityCard. Renders
 * label + amount + a short editorial gloss.
 */
export function CostStatusLine({ status, amount, currency, notes }) {
  const meta = STATUS_META[status];
  if (!meta) return null;
  const amt = formatAmount(amount, currency);
  return (
    <div className="flex items-baseline justify-between gap-3 py-2 border-t border-[rgba(0,0,0,0.04)]">
      <div>
        <CostStatusChip status={status} />
        {notes && (
          <p className="mt-1.5 font-accent italic text-[12.5px] text-[hsl(var(--foreground-muted))] max-w-xs">
            {notes}
          </p>
        )}
      </div>
      {amt && (
        <p className="font-serif text-[16px] sm:text-[17px] tabular-nums text-[hsl(var(--foreground))]">
          {amt}
        </p>
      )}
    </div>
  );
}

export { STATUS_META, formatAmount };
export default CostStatusChip;
